import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { TrendingUp, BarChart3 } from "lucide-react";
import { Fundraiser } from "../../../models/FundraisersGridModels";
import { CreateFundraiserModel } from "../../../models";

const ChartContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const ChartTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChartWrapper = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 24px 24px 16px 24px;
  height: 350px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  position: relative;
  width: 100%;
`;

const EmptyChartState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
  text-align: center;
`;

const EmptyChartIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin-bottom: 20px;
`;

const EmptyChartTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyChartText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const AreaChart = styled.svg`
  width: 100%;
  height: 100%;
`;

const ChartPath = styled.path`
  fill: url(#gradient);
  stroke: #10b981;
  stroke-width: 2;
`;
const Tooltip = styled.div`
  position: absolute;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  color: #1e293b;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  pointer-events: none;
  white-space: nowrap;
  transform: translate(-50%, -110%); // center and above
`;



interface DailyDonationsChartProps {
  fundraiser: CreateFundraiserModel;
}

export const DailyDonationsChart: React.FC<DailyDonationsChartProps> = observer(
  ({ fundraiser }) => {
    const formatShortDate = (date: Date) => {
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      }).format(date);
    };
    // 1) Refs
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const svgRef = React.useRef<SVGSVGElement>(null);
    const [svgWidth, setSvgWidth] = React.useState(800); // default


    // 2) Tooltip state uses CSS pixels
    const [tooltip, setTooltip] = React.useState<{
      left: number;
      top: number;
      date: Date;
      amount: number;
    } | null>(null);


    React.useEffect(() => {
      if (svgRef.current) {
        const resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            setSvgWidth(entry.contentRect.width);
          }
        });
        resizeObserver.observe(svgRef.current);
        return () => resizeObserver.disconnect();
      }
    }, []);


    // 3) Conversion from SVG (viewBox) to CSS pixels
    const toCssPos = (p: { x: number; y: number }) => {
      const svg = svgRef.current;
      const wrapper = wrapperRef.current;
      if (!svg || !wrapper) return { left: p.x, top: p.y };

      const svgRect = svg.getBoundingClientRect();
      const wrapRect = wrapper.getBoundingClientRect();

      // viewBox is 0 0 800 250
      const left =
        (p.x / 800) * svgRect.width + (svgRect.left - wrapRect.left);
      const top =
        (p.y / 250) * svgRect.height + (svgRect.top - wrapRect.top);

      return { left, top };
    };

    // // Generate daily donation amounts (not cumulative)
    const generateDailyData = () => {
      const today = new Date();
      const firstDonationDate = fundraiser.createdDate;

      // Calculate the date range - last 60 days or since first donation
      const daysSinceStart = Math.floor(
        (today.getTime() - firstDonationDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      const daysToShow = Math.min(daysSinceStart + 1, 60);

      const startDate = new Date(today);
      startDate.setDate(today.getDate() - (daysToShow - 1));

      // Generate daily donation amounts
      let data: { date: Date; amount: number }[] = [];
      const avgDailyAmount = fundraiser.totalRaisedAmount / daysToShow;

      for (let i = 0; i < daysToShow; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i);

        // Create realistic daily patterns
        let dayMultiplier = 1;
        const dayOfWeek = currentDate.getDay();

        // Weekend multiplier (lower donations on weekends)
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          dayMultiplier = 0.4;
        }

        // Random variation
        const randomMultiplier = 0.3 + Math.random() * 1.4;

        // Add some spikes for special days
        if (Math.random() > 0.9) {
          dayMultiplier *= 3;
        }

        const dailyAmount = avgDailyAmount * dayMultiplier * randomMultiplier;

        data.push({
          date: new Date(currentDate),
          amount: Math.max(0, Math.round(dailyAmount)),
        });
      }

      // Adjust the amounts so they total to the current campaign amount
      const totalGenerated = data.reduce((sum, d) => sum + d.amount, 0);
      const adjustmentRatio = fundraiser.totalRaisedAmount / totalGenerated;

      return data.map((d) => ({
        ...d,
        amount: Math.round(d.amount * adjustmentRatio),
      }));
    };

    // const dailyData = generateDailyData();
    // const maxAmount = Math.max(...dailyData.map((d) => d.amount));

    const maxAmount =
      fundraiser.dailyDonations?.reduce(
        (max, d) => (d.totalAmount > max ? d.totalAmount : max),
        0
      ) || 0;


    const hasNoDonations =
      fundraiser.totalRaisedAmount === 0 ||
      fundraiser.totalDonations === 0 ||
      maxAmount === 0;


    const dailyDonations = fundraiser.dailyDonations || [];

    const createPath = () => {
      if (dailyDonations.length === 0)
        return {
          areaPath: "",
          points: [],
        };

      const chartWidth = 800;
      const chartHeight = 250;
      const padding = 40;

      const points = dailyDonations.map((d, i) => {
        const x =
          padding +
          (i * (chartWidth - 2 * padding)) /
          (dailyDonations.length - 1);
        const y =
          chartHeight - 60 - (d.totalAmount / maxAmount) * (chartHeight - 100);
        return { x, y, date: d.date, amount: d.totalAmount };
      });

      const pathData = points
        .map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`))
        .join(" ");

      // Create area path
      const areaPath = `${pathData} L${points[points.length - 1].x},${chartHeight - 60} L${padding},${chartHeight - 60} Z`;

      return { areaPath, points };
    };

    const { areaPath } = createPath();
    const labelWidth = 50; // average width of a date label
    const maxLabels = Math.floor(svgWidth / labelWidth);
    const step = Math.ceil(dailyDonations.length / maxLabels);
    return (
      <ChartContainer>
        <ChartTitle>
          <TrendingUp size={28} color="#10b981" />
          Daily Donations
        </ChartTitle>
        <ChartWrapper ref={wrapperRef}>
          {hasNoDonations ? (
            <EmptyChartState>
              <EmptyChartIcon>
                <BarChart3 size={40} />
              </EmptyChartIcon>
              <EmptyChartTitle>No Donations Yet</EmptyChartTitle>
              <EmptyChartText>
                Donation activity will appear here once the
                <br />
                first donation is received.
              </EmptyChartText>
            </EmptyChartState>
          ) : (
            <>
              {tooltip && (
                <Tooltip style={{ left: tooltip.left, top: tooltip.top }}>
                  <div><strong>{formatShortDate(tooltip.date)}</strong></div>
                  <div>${tooltip.amount}</div>
                </Tooltip>
              )}

              <AreaChart viewBox="0 0 800 250" preserveAspectRatio="none" ref={svgRef}>
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop
                      offset="0%"
                      style={{ stopColor: "#10b981", stopOpacity: 0.3 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: "#10b981", stopOpacity: 0.05 }}
                    />
                  </linearGradient>
                </defs>

                <ChartPath d={areaPath} />
                {/* Circles for each point */}
                {createPath().points.map((p, i) => {
                  const prevX = i === 0 ? p.x : createPath().points[i - 1].x;
                  const nextX = i === createPath().points.length - 1 ? p.x : createPath().points[i + 1].x;

                  const left = (prevX + p.x) / 2;
                  const right = (p.x + nextX) / 2;

                  return (
                    <rect
                      key={i}
                      x={left}
                      y={0}
                      width={right - left}
                      height={250}

                      fill="transparent"
                      onMouseEnter={() => {
                        if (p.amount > 0) {
                          setTooltip({
                            left: toCssPos(p).left,
                            top: toCssPos(p).top,
                            date: p.date,
                            amount: p.amount,
                          })
                        } else {
                          setTooltip(null);
                        }
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  )
                })}
                {/* Date labels below the chart */}
                {dailyDonations.map((dataPoint, i) => {
                  // Decide how many labels to show
                  const maxLabels = 6; // or compute dynamically
                  const step = Math.ceil(dailyDonations.length / maxLabels);

                  if (i % step === 0 || i === dailyDonations.length - 1) {
                    // Use the same formula as in createPath()
                    //Since your chart path is built in the viewBox space (0 0 800 250), the labels should also 
                    //be positioned in that same space

                    const x =
                      40 +
                      (i * (800 - 2 * 40)) / (dailyDonations.length - 1); // <-- same math as points
                    return (
                      <text
                        key={i}
                        x={x}
                        y={235}
                        textAnchor="middle"
                        fontSize="12"
                        fill="#64748b"
                        fontWeight="500"
                      >
                        {formatShortDate(dataPoint.date)}
                      </text>
                    );
                  }
                  return null;
                })}

              </AreaChart>
            </>
          )}
        </ChartWrapper>
      </ChartContainer>
    );
  }
);
